import { RefObject } from 'react'

import { getUniqueId } from '@surglogs/util'

import { hasResponseErrorStatus } from '@surglogs-app/error'
import { useHandler } from '@surglogs-app/hooks'
import { useModal } from '@surglogs-app/prompt'

import { useToast } from '../../../ui.toast/src'
import { FileTypeGroup, FileTypesByGroup } from '../constants'
import { SupportedFilesModal } from '../modal'
import { UploadResponse } from '../types'

import { useUnsupportedFileToast } from './useUnsupportedFileToast'

export interface HandleFileUploadChangeValue extends UploadResponse {
  fileName: string
}

export interface HandleFileUploadChangeType {
  (
    value: Error | Partial<HandleFileUploadChangeValue> | undefined,
    fileName?: string,
  ): void
}

interface HandleFileUploadProps {
  fileTypesGroup: FileTypeGroup
  fileIdsRef: RefObject<number[]>
  handleChange: HandleFileUploadChangeType
  upload: (file: File) => Promise<UploadResponse>
  showFailureAlert?: boolean
}

export const useFileUploadHandler = ({
  handleChange,
  upload,
  fileTypesGroup,
  fileIdsRef,
  showFailureAlert = true,
}: HandleFileUploadProps) => {
  const [showSupportedFilesModal] = useModal(SupportedFilesModal)
  const { showUnsupportedFileToast } = useUnsupportedFileToast()
  const { showErrorToast } = useToast()

  return useHandler(async (file: File) => {
    const fileName = file.name
    const fileUploadId = getUniqueId()
    fileIdsRef.current.push(fileUploadId)

    // window.requestAnimationFrame(() => handleChange({ fileName }))
    handleChange({ fileName }, fileName)

    try {
      const value = await upload(file)

      // check whether user didn't cancel the upload
      if (fileIdsRef.current.includes(fileUploadId)) {
        fileIdsRef.current = fileIdsRef.current.filter(
          (id) => id !== fileUploadId,
        )

        if (value?.location) {
          handleChange({ ...value, fileName }, fileName)
        } else {
          showErrorToast({})
          handleChange(undefined, fileName)
        }
      }
    } catch (error) {
      console.error(error) // eslint-disable-line no-console

      // check whether user didn't cancel the upload
      if (fileIdsRef.current.includes(fileUploadId)) {
        fileIdsRef.current = fileIdsRef.current.filter(
          (id) => id !== fileUploadId,
        )

        // const error_code = path(['response', 'data', 'error_code'], error)
        // const status = path(['response', 'status'], error)
        // if (any(equals(error_code))(['invalid_input', 'invalid_content_input'])) {
        if (showFailureAlert) {
          if (hasResponseErrorStatus(error, 422)) {
            showUnsupportedFileToast({
              onSupportedFilesClick: () =>
                showSupportedFilesModal({
                  fileTypes: FileTypesByGroup[fileTypesGroup],
                }),
            })
          } else {
            showErrorToast({ error })
          }
        }

        handleChange(
          new Error((error as Error)?.message || (error as string)),
          fileName,
        )
      }
    }
  })
}
